import { useState, useEffect } from "react";
import {
  useParams, useDispatch, useSelector
} from "../../imports/generalImport";

import { Box, Container, Grid, Typography } from "../../imports/muiImport";
import { Helmet } from "react-helmet-async";

// API / Hooks
import { productDetailApiCall, } from "./productDetailHook";


// Redux
import {
  setProductDetails,
  wipeOutProductDetails,
} from "../../store/productInfo/action";
import {
  setNewDenomination,
  wipeOutDenomination,
} from "../../store/productDetails/action";

// Components
import PageBreadcrumb from "../../components/other/PageBreadcrumb";
import ProductDetailCard from "./productUtils/ProductDetailCard";
import PageNotFound from "../OTHERPAGE/PageNotFound";
import { actionNotifier } from "../../components/tostify";
import ProductDenomination from "./productUtils/ProductDenomination";
import GiftCardSkeleton from "../DASHBOARD/GiftCardSkeleton";
import SmilarGiftSectionComponent from "../../components/giftcardSections/similargift-section-component";
import ProdouctSeoInfo from "./productUtils/prodouctseoinfo";

function ProductDetail() {
  const [productDetail, setProductDetail] = useState({});
  const [productFound, setProductFound] = useState(false);
  const [cpId, setCPId] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const PRODUCTDETAILS = useSelector((state) => state.ProductDetail?.allData);
  const { id: urlId } = useParams();
  const dispatch = useDispatch();


  // -------------------------------
  // LOAD PRODUCT ON URL CHANGE
  // -------------------------------

  useEffect(() => {
    if (urlId !== cpId) {
      dispatch(wipeOutProductDetails());
      dispatch(wipeOutDenomination());
      getProductById(urlId);
      setCPId(urlId);
    }
  }, [urlId]);


  // -------------------------------
  // PAGE NOT FOUND
  // -------------------------------
  if (productFound) return <PageNotFound />;

  // -------------------------------
  // PAGE SEO HEAD
  // -------------------------------
  const seo = productDetail?.seo || {};

const canonicalUrl =
  seo?.canonicalUrl ||
  `https://www.99gift.ae/card-details/${productDetail?.slug}`;

const seoTitle =
  seo?.metaTitle ||
  seo?.title ||
  productDetail?.name ||
  "99Gift";

const seoDescription =
  seo?.metaDescription ||
  seo?.description ||
  productDetail?.description ||
  "";
  // -------------------------------
  // RENDER UI
  // -------------------------------
  return (
    <>
    <Helmet>
  {/* Title */}
  <title>{seoTitle}</title>

  {/* Meta Description */}
  {seoDescription && (
    <meta
      name="description"
      content={seoDescription}
    />
  )}

  {/* Keywords */}
  {seo?.keywords?.length > 0 && (
    <meta
      name="keywords"
      content={seo.keywords.join(", ")}
    />
  )}

  {/* Canonical */}
  <link
    rel="canonical"
    href={canonicalUrl}
  />

  {/* Open Graph */}
  <meta
    property="og:title"
    content={seo?.ogTitle || seoTitle}
  />

  <meta
    property="og:description"
    content={seo?.ogDescription || seoDescription}
  />

  <meta
    property="og:url"
    content={canonicalUrl}
  />

  <meta
    property="og:type"
    content="product"
  />

  {seo?.ogImage && (
    <meta
      property="og:image"
      content={seo.ogImage}
    />
  )}

  {/* Twitter */}
  <meta
    name="twitter:card"
    content="summary_large_image"
  />

  <meta
    name="twitter:title"
    content={seo?.ogTitle || seoTitle}
  />

  <meta
    name="twitter:description"
    content={seo?.ogDescription || seoDescription}
  />

  {seo?.ogImage && (
    <meta
      name="twitter:image"
      content={seo.ogImage}
    />
  )}

  <meta
    name="twitter:url"
    content={canonicalUrl}
  />
</Helmet>

      <Container maxWidth="lg" position="relative">
        <Grid
          container
          sx={{
            justifyContent: "center",
            rowGap: {
              xs: 2,
              md: 0
            },
            mt: {
              xs: 0,
              md: 6
            }
          }}
        >
          <Grid item xs={12} >
            <PageBreadcrumb home="Home" category={productDetail?.categories?.[0]?.slug} currentProduct={productDetail?.name} />
          </Grid>

          <Grid item xs={12} sm={12} md={6} lg={6} >
            <ProductDetailCard pageLoading={pageLoading} />
          </Grid>

          <Grid item xs={12} sm={12} md={6} lg={6}
            sx={{
              position: 'sticky',
              top: '90px',
              height: 'fit-content',
            }}
          >
            {PRODUCTDETAILS?.outOfStock ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: {
                    xs: '40px',
                    sm: '40px',
                    lg: '60px',
                  },
                  borderRadius: {
                    xs: 1,
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: {
                      xs: '1.2vw',
                      sm: '1.62vw',
                      lg: '1.8vw',
                    },
                    color: 'primary.main',
                    fontFamily: "Be Vietnam Pro"
                  }}
                >
                  Out of Stock
                </Typography>
              </Box>
            ) : (
              <ProductDenomination />
            )}
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ my: { xs: 2, md: 4 } }}>
        {PRODUCTDETAILS?.brandId ?
          <SmilarGiftSectionComponent
            sectionTitle={'Similar Gift Card'}
            sectionData={PRODUCTDETAILS?.brandId}
          />
          :
          <GiftCardSkeleton />
        }
      </Container>
      <Container maxWidth="lg" sx={{ my: { xs: 2, md: 4 } }}>
        <ProdouctSeoInfo />
      </Container>
    </>
  );

  // ===============================
  // FETCH PRODUCT FUNCTION
  // ===============================
  async function getProductById(urlId) {
    setPageLoading(true);

    try {
      const response = await productDetailApiCall(urlId);
      if (!response?.status) {
        setProductFound(true);
        actionNotifier.error(response?.message);
        return;
      }

      if (!response?.data?.rangeDenomination) {
        dispatch(setNewDenomination(response?.data?.newDeno));
      }

      setProductDetail(response?.data);
      dispatch(setProductDetails(response?.data));
    } catch (err) {
      console.error(err);
    }
    finally {
      setPageLoading(false);
    }
  }
}

export default ProductDetail;
